// Booking RPC wrappers shared by the widget and the staff app (parity
// Phase 0.5). One source of truth: both apps used to carry hand-mirrored
// copies of these signatures, which drifted (the staff copy discarded rule
// context and couldn't send seating areas). Every function takes the app's
// own Supabase client as its first argument.

import type { BookingClient, RpcResult } from "./client";

export type BookingWindow = {
  starts_at: string; // ISO
  ends_at: string;
  party_size: number;
};

export type CapacityResponse =
  | {
      ok: true;
      service_remaining: number | null;
      plan_remaining: number | null;
    }
  | {
      ok: false;
      reason:
        | "invalid_party_size"
        | "invalid_window"
        | "service_unavailable"
        | "plan_unavailable"
        | "seat_taken"
        | "service_capacity_exceeded"
        | "plan_capacity_exceeded"
        | string;
      service_remaining?: number;
      plan_remaining?: number;
    };

export async function checkCapacity(
  client: BookingClient,
  input: {
    serviceId: string;
    floorPlanId: string | null;
    elementId: string | null;
    window: BookingWindow;
  },
): Promise<CapacityResponse> {
  const { data, error } = await client.rpc("check_capacity_for_slot", {
    p_service_id: input.serviceId,
    p_floor_plan_id: input.floorPlanId,
    p_floor_plan_element_id: input.elementId,
    p_starts_at: input.window.starts_at,
    p_ends_at: input.window.ends_at,
    p_party_size: input.window.party_size,
  });
  if (error || !data) {
    return { ok: false, reason: error?.message ?? "network_error" };
  }
  return data as CapacityResponse;
}

// Failure payload can carry rich context from the rule resolvers:
//   { ok:false, reason:'blackout_dow', rule_name:'Closed Tuesdays', blocked_date:'...' }
//   { ok:false, reason:'min_stay', rule_name:'...', min_nights:2, actual_nights:1 }
// describeReason() presents a specific message when these fields exist.
export type HoldFailure = {
  ok: false;
  reason: string;
  rule_name?: string;
  blocked_date?: string;
  range_start?: string;
  range_end?: string;
  min_nights?: number;
  max_nights?: number;
  actual_nights?: number;
  min_lead_hours?: number;
  actual_lead_hours?: number;
  /** availability_rules kind min_lead_minutes (exact notice). */
  min_lead_minutes?: number;
  actual_lead_minutes?: number;
  max_advance_days?: number;
  actual_advance_days?: number;
  /**
   * resource_unavailable detail ('unit_required' | 'all_units_blocked').
   * The shortfall/pool counts the RPC also returns are deliberately NOT
   * typed: they must never reach a guest.
   */
  scope?: string;
  /**
   * The server's own wording for the refusal. A reason RETURNED as jsonb
   * (menu_split_mismatch) carries it in the payload; one RAISED by a trigger
   * (party_not_allowed / party_out_of_range from
   * trg_enforce_special_date_party) arrives as the PostgREST error hint and
   * createBookingHold copies it here.
   */
  hint?: string;
};

export type HoldResponse =
  | {
      ok: true;
      booking_id: string;
      /** Human-friendly code (LAB-260516-K3PTRN); absent on older RPCs. */
      reference?: string;
      amount_cents: number;
      requires_full_payment: boolean;
      special_date_event_id: string | null;
      seating_area_id: string | null;
      /**
       * The window actually held (20260913090020). An option with its own
       * length (service_units.config.duration_minutes) is normalised to
       * starts_at + that length whatever ends_at was sent. Absent on
       * event-only holds and older RPCs.
       */
      starts_at?: string;
      ends_at?: string;
      /** Full native unit/person price written by the hold, excluding checkout addons. */
      unit_price_cents?: number;
      /**
       * Special dates with two or more guest menus (20260925090010): the
       * per-menu split the hold validated and priced, as stored on the
       * booking. Absent or empty otherwise.
       */
      special_date_menu_lines?: SpecialDateMenuLine[];
    }
  | HoldFailure;

/** One line of a special-date menu split, priced server-side. */
export type SpecialDateMenuLine = {
  menu_id: string;
  menu_key: string;
  name: string;
  qty: number;
  unit_price_cents: number;
};

/** What a caller sends for the split: how many guests take each menu. */
export type SpecialDateMenuChoice = { menu_id: string; qty: number };

export async function createBookingHold(
  client: BookingClient,
  input: {
    serviceId: string;
    floorPlanId: string | null;
    elementId: string | null;
    serviceUnitId: string | null;
    seatingAreaId?: string | null;
    window: BookingWindow;
    guest: { name: string | null; email: string | null; phone: string | null };
    cartToken: string;
    holdMinutes?: number;
    /**
     * Required when the window is a special date with two or more guest
     * menus; the server refuses `menu_selection_required` otherwise. Every
     * surface (widget, staff app, bot) sends the same shape.
     */
    menuLines?: SpecialDateMenuChoice[] | null;
  },
): Promise<HoldResponse> {
  const { data, error } = await client.rpc("create_booking_hold", {
    p_service_id: input.serviceId,
    p_floor_plan_id: input.floorPlanId,
    p_floor_plan_element_id: input.elementId,
    p_service_unit_id: input.serviceUnitId,
    p_seating_area_id: input.seatingAreaId ?? null,
    p_starts_at: input.window.starts_at,
    p_ends_at: input.window.ends_at,
    p_party_size: input.window.party_size,
    p_guest_name: input.guest.name,
    p_guest_email: input.guest.email,
    p_guest_phone: input.guest.phone,
    p_cart_token: input.cartToken,
    p_hold_minutes: input.holdMinutes ?? 15,
    ...(input.menuLines && input.menuLines.length > 0
      ? { p_menu_lines: input.menuLines }
      : {}),
  });
  if (error || !data) {
    return {
      ok: false,
      reason: error?.message ?? "network_error",
      ...(error?.hint ? { hint: error.hint } : {}),
    };
  }
  return data as HoldResponse;
}

/**
 * extend_cart_hold (20260927090010). Keeps a widget cart's holds alive while
 * the guest is still filling in a long checkout form. Each call moves every
 * live hold to at most now + 10 minutes, capped at the cart's first hold +
 * 45 minutes and at the earliest start − 30 minutes. It never shortens a
 * hold and never revives a lapsed one.
 *
 * `bookingIds` must be exactly the cart's clocked pending holds as the page
 * shows them; any difference is refused `cart_mismatch` (with the server's
 * ids) so a stray row the checkout would also charge is never kept alive.
 * Managed-deposit rows may be sent or left out. A combo line must send every
 * component booking id. A spa item has no server row before payment and
 * cannot be extended.
 *
 * The caps bound what THIS call grants, not a hold's life: a hold armed longer
 * by another path (create/update_booking_hold take the caller's minutes) is
 * never shortened, so `hold_expires_at` can be later than `cap_at`.
 *
 * Callers: `busy` means another transaction (a checkout, the payment webhook,
 * the expiry sweep) holds the cart's rows right now; keep the current expiry
 * and retry on the next tick. `hold_expired` can name a lapsed row the page
 * never listed (a stray): compare its `booking_ids` with the page's before
 * telling the guest their hold ended. `nothing_held` and the managed-only
 * `ok` with null times are not in brief section 5.6; both are deliberate.
 *
 * Called by the widget's checkout drawer (apps/widget/src/state/
 * use-hold-keepalive.ts) under the rules in hold-timer-logic.ts; the stepped
 * checkout (W1) can reuse both.
 */
export type ExtendCartHoldResult =
  | {
      ok: true;
      /** The cart's effective expiry: its earliest live hold. Null for a cart of managed deposits only. */
      hold_expires_at: string | null;
      /** The latest any call can push the hold to. May already be in the past. */
      cap_at: string | null;
      /** True when at least one hold moved later. */
      extended: boolean;
    }
  | ExtendCartHoldRefusal;

export type ExtendCartHoldRefusal =
  | {
      ok: false;
      /**
       * invalid_cart_token: missing or too short.
       * not_extendable: a staff pay-link or managed batch cart.
       * nothing_held: the token has no pending holds.
       * busy: another transaction holds the cart's rows; nothing was
       * extended. Retry on the next tick; never read it as a lapse.
       */
      reason: "invalid_cart_token" | "not_extendable" | "nothing_held" | "busy";
    }
  | {
      ok: false;
      /**
       * hold_expired: these holds already lapsed; nothing was extended.
       * cart_mismatch: the page's ids differ from the cart's live holds;
       * `booking_ids` are the server's. Nothing was extended.
       */
      reason: "hold_expired" | "cart_mismatch";
      booking_ids: string[];
    }
  | {
      ok: false;
      /**
       * The call failed in transport or returned nothing readable. The
       * server may or may not have extended: treat it as unknown, never as
       * a lapse, and try again on the next tick.
       */
      reason: "unknown";
      message: string;
    };

export async function extendCartHold(
  client: BookingClient,
  cartToken: string,
  bookingIds: readonly string[],
): Promise<ExtendCartHoldResult> {
  const { data, error } = await client.rpc("extend_cart_hold", {
    p_cart_token: cartToken,
    p_booking_ids: [...bookingIds],
  });
  if (error || !data || typeof data !== "object") {
    return { ok: false, reason: "unknown", message: error?.message ?? "no_response" };
  }
  return data as ExtendCartHoldResult;
}

/**
 * get_cart_hold_state (20260927145010). READ-only: when the named holds of a
 * cart lapse, on the server's clock. The checkout drawer reads it to know when
 * to show the hold timer (a cart with a spa item too, which must never call
 * extendCartHold) and to confirm a lapse before saying so.
 *
 * `holds` has one entry per named row of THIS cart token; another cart's row,
 * or an id with no row, is absent. `hold_expires_at` is the earliest `held`
 * row's expiry, null when none is held. `now` is the server's clock.
 */
export type CartHoldRowState = "held" | "lapsed" | "managed" | "gone";

export type CartHoldStateResult =
  | {
      ok: true;
      now: string;
      hold_expires_at: string | null;
      holds: { booking_id: string; state: CartHoldRowState; hold_expires_at: string | null }[];
    }
  | { ok: false; reason: "invalid_cart_token" | "too_many_ids" }
  | { ok: false; reason: "unknown"; message: string };

export async function getCartHoldState(
  client: BookingClient,
  cartToken: string,
  bookingIds: readonly string[],
): Promise<CartHoldStateResult> {
  const { data, error } = await client.rpc("get_cart_hold_state", {
    p_cart_token: cartToken,
    p_booking_ids: [...bookingIds],
  });
  if (error || !data || typeof data !== "object") {
    return { ok: false, reason: "unknown", message: error?.message ?? "no_response" };
  }
  return data as CartHoldStateResult;
}

/**
 * save_booking_participant_row / save_payment_link_participant_row
 * (20260927160010, 20260927160510, re-keyed by rider id in 20261001110020):
 * save ONE rider's answers as the guest goes, instead of the whole list at
 * the end (save_booking_participants / save_payment_link_participants, which
 * stay).
 *
 * The rider is its id (`_pid` in the row, participant-ids.ts), never its
 * place: a row with an id must be that rider's, stored at `position` (else
 * participant_unknown / participant_order_changed); a row WITHOUT an id is a
 * new rider and may only fill a slot nobody is in yet (it takes that slot's
 * id; over a rider who holds answers it is participant_missing). The reply
 * names the rider's id (`participant_id`): write it onto the draft row that
 * was sent, so every later save of that rider carries it. `position` is the
 * rider's 1-based number on the form. The row is sent exactly as given (pass
 * questionsPayload's row: it carries _pid and the markers, never _key).
 *
 * The server stores the row only when it is valid ON ITS OWN, or not at all
 * (a partial row is refused invalid_participant_details), pads the list with
 * {} ("not in yet") up to the party, and replies with the riders still
 * outstanding. Payment still needs every rider complete: the gate is
 * unchanged. Who may save is exactly who may save the whole list. A retry
 * of the same row at the same position is harmless.
 *
 * `expected` guards a stale page (two tabs; the checkout on a laptop and the
 * pay link on a phone): send the rider as this page last read it, UNTOUCHED
 * — the reader's row at this position ({} when it returned none), or the
 * `row` of this rider's previous save's reply. If another save has put a
 * different rider there since, the save is refused `rider_changed` and
 * nothing is stored: read the forms again. It is judged per rider (saves of
 * other riders do not make it stale) and only on what this caller's reader
 * returns, so a private answer changed elsewhere is not a conflict. null
 * skips the check: only for a caller with no page copy (a staff tool).
 *
 * A save that moves a rider to another number (a swap, as one-rider saves)
 * carries none of their private answers: a guest types them again, a
 * member below manager is refused sensitive_answers_need_manager.
 *
 * Used by the widget's full-page checkout and the drawer's one-rider pager
 * on a phone (each rider saved as the guest taps Next, Back or another
 * rider's chip: apps/widget/src/state/rider-autosave.ts). The desktop
 * drawer (every rider at once) and the pay-link page still save the whole
 * list.
 */
export type ParticipantRowSaved = {
  ok: true;
  /** The 1-based position saved. */
  position: number;
  /**
   * The rider's permanent id (20261001110020): adopt it onto the row that
   * was sent (participantIdOfReply). Absent from a server before P3a.
   */
  participant_id?: string;
  /** The 1-based positions that still hold no answers, in order. */
  outstanding: number[];
  /**
   * This rider as this caller's reader now returns it (private answers held
   * back below manager): send it as `expected` on this rider's next save.
   * A removal (a tombstone, 20261002100020) carries it only for Replace (the
   * new rider not in yet); "not coming" has none.
   */
  row: Record<string, unknown>;
  /**
   * A tombstone's reply (20261002100020): the id removed. Replace: its place
   * holds a new rider not in yet, whose id is `participant_id`; "not coming"
   * (more riders than the party): the rider left the list, `participant_id`
   * is null and the riders after it moved up one number.
   */
  removed?: string[];
};

export type ParticipantRowRefusalReason =
  /** No live hold for this cart token, or not a member who may edit. */
  | "not_authorized"
  | "form_not_found"
  /** Below 1 or above the booking's party size. */
  | "invalid_position"
  /** No answers ({}, only a rider id, only nulls or blanks), or not an object: nothing to save. */
  | "empty_row"
  /** A saved rider sits beyond the party (the headcount went down): staff must sort it out. */
  | "rows_out_of_line"
  /**
   * (P4a) A saved rider the party still holds was sent as a bare tombstone on
   * a list with a required question: they are replaced (the new rider in the
   * same save), never just removed.
   */
  | "replacement_required"
  /** Another save put a different rider at this position since the page read it (`expected`): read again. */
  | "rider_changed"
  /** The row is not valid on its own (a required answer missing, a hidden one answered, an unknown key). */
  | "invalid_participant_details"
  /** A member below manager sent, cleared or needed a private answer. */
  | "sensitive_answers_need_manager"
  /** The row's id is not stored on this booking (made up, removed, another booking's): read again. */
  | "participant_unknown"
  /** The row's id belongs to the rider at another number: read again. */
  | "participant_order_changed"
  /** A row without an id where a saved rider is: name the rider by its id (read again). */
  | "participant_missing"
  /** A saved rider's name changed on a list with private questions, without "_same_person": true. */
  | "identity_changed"
  /** "_self" naming a saved person, or "_pick": not possible yet (G2, P4). */
  | "participant_pick_not_allowed";

/**
 * No reply the page can act on. `network`: no answer came back at all (a
 * lost signal, or the call's `signal` gave up waiting), so the save may or
 * may not have been stored; false: the server answered with an error of its
 * own (a 500, a statement timeout, PGRST202 for a function not deployed
 * yet), which is not the guest's connection.
 */
export type ParticipantRowUnknown = { ok: false; reason: "unknown"; message: string; network: boolean };

export type SaveParticipantRowResult =
  | ParticipantRowSaved
  | {
      ok: false;
      /** details_on_secure_page: service_role (the bot) on a form it may not collect in chat. */
      reason: ParticipantRowRefusalReason | "details_on_secure_page";
      /** The riders a refusal names, by id (the id refusals, identity_changed, a staff refusal). */
      participant_ids?: string[];
    }
  | ParticipantRowUnknown;

/** The pay link's scope refusals (_reservation_participant_scope), as save_payment_link_participants returns them. */
export type PaymentLinkScopeRefusalReason =
  | "invalid_token"
  | "booking_not_found"
  | "atomic_batch_incomplete"
  | "already_confirmed"
  | "hold_expired"
  | "multi_booking_cart_unsupported";

export type SavePaymentLinkParticipantRowResult =
  | ParticipantRowSaved
  | { ok: false; reason: ParticipantRowRefusalReason | PaymentLinkScopeRefusalReason; participant_ids?: string[] }
  | ParticipantRowUnknown;

export type ParticipantRowInput = {
  bookingId: string;
  /** 1-based rider number on the form. */
  position: number;
  /** One rider's answers, as questionsPayload builds them (with the rider's _pid once it has one). */
  row: Readonly<Record<string, unknown>>;
  /**
   * The rider as this page last read it, untouched: the reader's row at this
   * position ({} for none) or the previous reply's `row`. null skips the
   * stale-page check (see above).
   */
  expected: Readonly<Record<string, unknown>> | null;
  /**
   * Give up waiting when this aborts (a page's own time limit): the call
   * then answers { reason: "unknown", network: true } at once. The request
   * itself is cancelled where the client can (supabase-js's abortSignal);
   * the server may still have stored the row, exactly as when the signal is
   * lost after the request left.
   */
  signal?: AbortSignal;
};

// A fetch that never reached the server, or never came back: supabase-js
// answers status 0 and names the fetch error ("TypeError: Failed to
// fetch", "AbortError: …"); a client that gives no status is judged by
// those names.
const NETWORK_ERROR = /^(TypeError|FetchError|AbortError)\b|failed to fetch|fetch failed|networkerror|load failed/i;

function participantRowReply<R>(reply: RpcResult): R | ParticipantRowUnknown {
  const { data, error, status } = reply;
  if (error || !data || typeof data !== "object") {
    const network = status === 0 || (status === undefined && !!error && NETWORK_ERROR.test(error.message));
    return { ok: false, reason: "unknown", message: error?.message ?? "no_response", network };
  }
  return data as R;
}

/** One call, cut short when `signal` aborts (see ParticipantRowInput.signal). */
async function rowCall(
  client: BookingClient,
  fn: string,
  args: Record<string, unknown>,
  signal: AbortSignal | undefined,
): Promise<RpcResult> {
  const gaveUp: RpcResult = { data: null, error: { message: "AbortError: the save took too long" }, status: 0 };
  if (!signal) return client.rpc(fn, args);
  if (signal.aborted) return gaveUp;
  const call = client.rpc(fn, args) as PromiseLike<RpcResult> & {
    abortSignal?: (signal: AbortSignal) => PromiseLike<RpcResult>;
  };
  const cancellable = typeof call.abortSignal === "function" ? call.abortSignal(signal) : call;
  const aborted = new Promise<RpcResult>((resolve) => signal.addEventListener("abort", () => resolve(gaveUp), { once: true }));
  return Promise.race([Promise.resolve(cancellable), aborted]);
}

/** The checkout (its cart token), a signed-in staff member (cartToken null) or the bot. */
export async function saveBookingParticipantRow(
  client: BookingClient,
  input: ParticipantRowInput & { cartToken: string | null },
): Promise<SaveParticipantRowResult> {
  const reply = await rowCall(
    client,
    "save_booking_participant_row",
    {
      p_booking_id: input.bookingId,
      p_cart_token: input.cartToken,
      p_position: input.position,
      p_row: input.row,
      p_expected: input.expected,
    },
    input.signal,
  );
  return participantRowReply<SaveParticipantRowResult>(reply);
}

/** The hosted pay page (/pay/<token>). */
export async function savePaymentLinkParticipantRow(
  client: BookingClient,
  input: ParticipantRowInput & { token: string },
): Promise<SavePaymentLinkParticipantRowResult> {
  const reply = await rowCall(
    client,
    "save_payment_link_participant_row",
    {
      p_token: input.token,
      p_booking_id: input.bookingId,
      p_position: input.position,
      p_row: input.row,
      p_expected: input.expected,
    },
    input.signal,
  );
  return participantRowReply<SavePaymentLinkParticipantRowResult>(reply);
}

export async function fetchSeatAvailability(
  client: BookingClient,
  input: { floorPlanId: string; window: BookingWindow },
): Promise<Set<string>> {
  const { data, error } = await client.rpc("seat_availability", {
    p_floor_plan_id: input.floorPlanId,
    p_starts_at: input.window.starts_at,
    p_ends_at: input.window.ends_at,
  });
  if (error || !data) return new Set();
  return new Set(
    (data as { element_id: string }[]).map((r) => r.element_id).filter(Boolean),
  );
}

// Cancels a PENDING hold by booking id + cart token (bearer pair). Called
// when a guest removes a cart line so checkout can't charge a booking the
// guest no longer wants (RPC added in 20260712000040).
export async function cancelCartBooking(
  client: BookingClient,
  bookingId: string,
  cartToken: string,
): Promise<void> {
  await client.rpc("cancel_cart_booking", {
    p_booking_id: bookingId,
    p_cart_token: cartToken,
  });
}
